import React, { useState, useEffect, useId, useRef } from 'react';
import { encode } from 'blurhash';
import { FiAlertCircle, FiImage, FiLink, FiTrash2, FiUpload } from 'react-icons/fi';
import { ImageData } from '../../../../model/FestivalData';
import { getImageUrl } from '../../../../services/imageService';
import { useMessages } from '../../../../i18n/I18n';
import './ImageEditor.css';

interface ImageEditorProps {
  currentImage?: ImageData;
  onChange: (newImage: ImageData | undefined) => void;
  getApiImageUrl: (identifier: string) => string;
  tempPreviewUrl?: string;
  fillMode?: 'cover' | 'contain';
  title?: string;
  caption?: string;
}

const ImageEditor: React.FC<ImageEditorProps> = ({ currentImage, onChange, getApiImageUrl, tempPreviewUrl, fillMode = 'cover', title, caption }) => {
  const t = useMessages('imagePicker');
  const common = useMessages('common');
  const [imageType, setImageType] = useState<'ApiImage' | 'ExternalLink'>(
    currentImage?.type || 'ApiImage'
  );
  const [file, setFile] = useState<File | null>(null);
  const [externalUrl, setExternalUrl] = useState<string>('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  // A key of t.errors, so that the message follows the language shown
  const [error, setError] = useState<keyof typeof t.errors | null>(null);
  // The last preview that finished loading (or failed to), and the blurhashes still being computed
  const [settledPreviewUrl, setSettledPreviewUrl] = useState<string | null>(null);
  const [pendingBlurhashes, setPendingBlurhashes] = useState(0);
  const [isSlow, setIsSlow] = useState(false);
  const [isDropTarget, setIsDropTarget] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const urlInputRef = useRef<HTMLInputElement>(null);
  const focusUrlInput = useRef(false);
  const titleId = useId();

  useEffect(() => {
    if (currentImage) {
      if (currentImage.type === 'ApiImage') {
        if ('file' in currentImage && currentImage.file) {
          setFile(currentImage.file);
          setPreviewUrl(URL.createObjectURL(currentImage.file));
        } else if (currentImage.identifier) {
          const url = getImageUrl(currentImage, getApiImageUrl);
          setPreviewUrl(url || null);
        }
        setExternalUrl('');
      } else {
        setExternalUrl(currentImage.url);
        setPreviewUrl(currentImage.url);
        setFile(null);
      }
    } else {
      setFile(null);
      setExternalUrl('');
      setPreviewUrl(tempPreviewUrl || null);
    }

    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [currentImage, getApiImageUrl, tempPreviewUrl]);

  // "Use a link" reveals the URL box: put the cursor in it
  useEffect(() => {
    if (imageType === 'ExternalLink' && focusUrlInput.current) {
      focusUrlInput.current = false;
      urlInputRef.current?.focus();
    }
  }, [imageType]);

  const handleTypeChange = (newType: 'ApiImage' | 'ExternalLink') => {
    // Only make changes if type is actually changing
    if (newType !== imageType) {
      setImageType(newType);
      setError(null);

      if (newType === 'ApiImage') {
        setExternalUrl('');
        setPreviewUrl(null);
        setFile(null);
        onChange(undefined);
      } else {
        setFile(null);
        setPreviewUrl(null);
        setExternalUrl('');
        onChange(undefined);
      }
    }
  };

  const validateUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const loadImage = (src: string): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  };

  const getImageData = (image: HTMLImageElement): Uint8ClampedArray => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d')!;
    canvas.width = 32;  // small size for blurhash
    canvas.height = Math.floor((image.height * 32) / image.width);

    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
    return imageData.data;
  };

  const computeBlurhash = async (imageUrl: string): Promise<string | undefined> => {
    try {
      const img = await loadImage(imageUrl);
      const pixels = getImageData(img);

      const blurhash = encode(
        pixels,
        32, // width
        Math.floor((img.height * 32) / img.width), // height
        4,  // componentX
        3   // componentY
      );

      return blurhash;
    } catch (error) {
      console.error('Error computing blurhash:', error);
      return undefined;
    }
  };

  const handleUrlChange = async (value: string) => {
    setExternalUrl(value);
    if (value && !validateUrl(value)) {
      setError('invalidUrl');
      setPreviewUrl(null);
    } else {
      setError(null);
      setPreviewUrl(value);

      if (value) {
        setPendingBlurhashes(count => count + 1);
        const blurhash = await computeBlurhash(value);
        onChange({
          type: 'ExternalLink',
          url: value,
          ...(blurhash ? { blurhash } : {})
        });
        setPendingBlurhashes(count => count - 1);
      } else {
        onChange(undefined);
      }
    }
  };

  const handleFile = async (selectedFile: File | undefined) => {
    if (selectedFile) {
      setError(null);
      setFile(selectedFile);
      const localPreviewUrl = URL.createObjectURL(selectedFile);
      setPreviewUrl(localPreviewUrl);

      setPendingBlurhashes(count => count + 1);
      const blurhash = await computeBlurhash(localPreviewUrl);
      const newImageData: ImageData = {
        type: 'ApiImage',
        file: selectedFile,
        identifier: '',
        ...(blurhash ? { blurhash } : {})
      };

      onChange(newImageData);
      setPendingBlurhashes(count => count - 1);
    } else {
      setFile(null);
      setPreviewUrl(null);
      onChange(undefined);
    }
    setExternalUrl('');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    // Emptied, so that picking the same file again is still a change
    e.target.value = '';
    handleFile(selectedFile);
  };

  // Like switching to Upload, it drops a linked image, then opens the file picker
  const handleUploadClick = () => {
    handleTypeChange('ApiImage');
    fileInputRef.current?.click();
  };

  const handleUseLinkClick = () => {
    focusUrlInput.current = true;
    handleTypeChange('ExternalLink');
  };

  const handleRemove = () => {
    setFile(null);
    setPreviewUrl(null);
    setExternalUrl('');
    setError(null);
    onChange(undefined);
  };

  const isDraggingFiles = (e: React.DragEvent) => e.dataTransfer.types.includes('Files');

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (!isDraggingFiles(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDropTarget(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setIsDropTarget(false);
  };

  // A file dropped on the preview is uploaded, as if picked with "Upload…"
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (!isDraggingFiles(e)) return;
    e.preventDefault();
    setIsDropTarget(false);
    const droppedFile = Array.from(e.dataTransfer.files).find(dropped => dropped.type.startsWith('image/'));
    if (!droppedFile) {
      setError('notAnImage');
      return;
    }
    setImageType('ApiImage');
    handleFile(droppedFile);
  };

  const hasImage = Boolean(previewUrl || externalUrl || file);
  const isBusy = Boolean(previewUrl && settledPreviewUrl !== previewUrl) || pendingBlurhashes > 0;

  // "Loading image…" only for slow images, so that opening a panel doesn't flash it
  useEffect(() => {
    setIsSlow(false);
    if (!isBusy) return;
    const timer = setTimeout(() => setIsSlow(true), 400);
    return () => clearTimeout(timer);
  }, [isBusy]);

  return (
    <div
      className="image-editor"
      role={title ? 'group' : undefined}
      aria-labelledby={title ? titleId : undefined}
      aria-busy={isBusy}
    >
      {title && <div id={titleId} className="image-editor-title">{title}</div>}
      <div
        className={`image-editor-preview${previewUrl ? '' : ' is-empty'}${isDropTarget ? ' is-drop-target' : ''}`}
        onDragEnter={handleDragOver}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={t.previewAlt}
            className={`image-editor-img image-${fillMode}`}
            onLoad={() => setSettledPreviewUrl(previewUrl)}
            onError={() => {
              setSettledPreviewUrl(previewUrl);
              setError('loadFailed');
            }}
          />
        ) : (
          <div className="image-editor-empty">
            <FiImage aria-hidden="true" />
            <span>{t.empty}</span>
          </div>
        )}
        {isDropTarget && <div className="image-editor-drop-hint">{t.dropHint}</div>}
      </div>

      <div className="image-editor-actions">
        <button type="button" className="btn" onClick={handleUploadClick}>
          <FiUpload aria-hidden="true" />
          {t.upload}
        </button>
        {imageType === 'ApiImage' && (
          <button type="button" className="btn" onClick={handleUseLinkClick}>
            <FiLink aria-hidden="true" />
            {t.useLink}
          </button>
        )}
        {hasImage && (
          <button type="button" className="btn btn-ghost image-editor-remove" aria-label={t.removeLabel} onClick={handleRemove}>
            <FiTrash2 aria-hidden="true" />
            {common.remove}
          </button>
        )}
      </div>

      {imageType === 'ExternalLink' && (
        <div className="image-editor-url">
          <FiLink aria-hidden="true" />
          <input
            ref={urlInputRef}
            type="text"
            inputMode="url"
            className="image-editor-url-input"
            value={externalUrl}
            onChange={(e) => handleUrlChange(e.target.value)}
            placeholder="https://example.com/image.png"
            aria-label={t.urlLabel}
            aria-invalid={error === 'invalidUrl'}
          />
        </div>
      )}

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        hidden
        accept="image/*"
      />

      {isBusy && isSlow && (
        <div className="image-editor-status">
          <span className="image-editor-spinner" aria-hidden="true" />
          {t.loading}
        </div>
      )}
      {error && (
        <div className="image-editor-error" role="alert">
          <FiAlertCircle aria-hidden="true" />
          {t.errors[error]}
        </div>
      )}
      {caption && <div className="image-editor-caption">{caption}</div>}
    </div>
  );
};

export default ImageEditor;
